import type { GroupedFilterOption } from '@models/Filter'
import type { GroupedOption } from '@models/GroupedOption'

import type { Project } from '../../aansluiting/project/types/Project'
import type { AuthenticatedUser } from '../../auth/types/AuthenticatedUser'
import type { SubprocesTemplate } from '../../entity/subprocessen/types/SubprocesTemplate'
import type { HoofdleidingProject } from '../../hoofdleiding/project/types/HoofdleidingProject'

import { alDisciplines, hlDisciplines } from './discipline'

export const getPossibleAssigneesForProject = ({
  project,
}: {
  project: Project | HoofdleidingProject
}) => {
  const { combi, coordAannemer, netbeheerdersContract, aannemers } = project
  const buildOption = (group) => (label) => ({ group, label })

  return [buildOption('Combi')(combi)]
    .concat(coordAannemer?.map(buildOption('Coordinerende')) || [])
    .concat(netbeheerdersContract?.map(buildOption('Netbeheerder')) || [])
    .concat(
      aannemers?.map(buildOption('Aannemer')) || [],
    ) as GroupedFilterOption[]
}

export const canCreateAssignedBijlageForProject = ({
  project,
  user,
  assignees,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser | Record<string, any>
  assignees: GroupedOption[]
}): boolean => {
  const allowedStatusTextAssignedBijlage = [
    'Intake',
    'Werkvoorbereiding',
    'In uitvoering',
  ]
  const { type, statusText } = project
  const isHoofdleidingProject = type === 'Hoofdleiding'
  const isPossibleAssignee = assignees.some(
    (option: GroupedOption) => option.label === user.organisatie,
  )

  if (!user.canCreateAssignedBijlage) return false
  if (isHoofdleidingProject)
    return project.verantwoordelijken.includes(user.organisatie)

  return (
    isPossibleAssignee && allowedStatusTextAssignedBijlage.includes(statusText)
  )
}

export const canChangeProjectForProject = ({
  project,
  user,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser | Record<string, any>
}): boolean => {
  const { type, verantwoordelijken } = project
  const isHoofdleidingProject = type === 'Hoofdleiding'

  return (
    isHoofdleidingProject &&
    !!user.canChangeProject &&
    verantwoordelijken.includes(user.organisatie)
  )
}

export const explodeKnownProjects = (
  linkedProjects: string[] | undefined, // can be undefined, so this is to ensure typing matches that
  accessProjectList: Project[],
) => {
  if (!linkedProjects || !linkedProjects.length) return []

  return linkedProjects.map(
    (id) =>
      accessProjectList.find((x) => x.aanvraagID === id) || { aanvraagID: id },
  )
}

export const isUserVerantwoordelijkForProject = ({
  user,
  project,
}: {
  user: AuthenticatedUser | Record<string, any>
  project: Project | HoofdleidingProject
}) => {
  return project.verantwoordelijken.includes(user.organisatie)
}

export const isUserAannemerForProject = ({
  user,
  project,
}: {
  user: AuthenticatedUser | Record<string, any>
  project: Project | HoofdleidingProject
}) => {
  return Boolean(
    project.aannemers?.includes(user.organisatie) ||
    project.coordAannemer?.includes(user.organisatie),
  )
}

export const canUserCreateSubprocesForProject = ({
  project,
  user,
  templates,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser
  templates: SubprocesTemplate[]
}) => {
  const canCreateSchouw =
    user.canCreateSubprocesSchouw &&
    templates.some(
      (template) =>
        template.type === 'schouw' && template.projectType === project.type,
    )
  const canCreateBodem =
    user.canCreateSubprocesBodem &&
    templates.some(
      (template) =>
        template.type === 'bodem' && template.projectType === project.type,
    )
  const canCreateNetcheck =
    user.canCreateSubprocesNetcheck &&
    templates.some(
      (template) =>
        template.type === 'netcheck' && template.projectType === project.type,
    )

  return canCreateSchouw || canCreateBodem || canCreateNetcheck
}

export const updatableProps = [
  'gemeente',
  'naam',
  'typeWerk',
  'externeReferentie',
  'adres',
  'tracelengte',
  'woningen',
  'appartementen',
  'bedrijfsunits',
  'aanvraagDatum',
  'wensweek',
]

export const buildProjectDetailsUpdates = <T = Project | HoofdleidingProject>({
  baseProject = {},
  updatedProject,
}: {
  baseProject?: Partial<T>
  updatedProject: Partial<T>
}): Partial<T> => {
  return updatableProps.reduce((acc, key) => {
    if (baseProject[key] !== updatedProject[key]) {
      acc[key] = updatedProject[key]
    }

    return acc
  }, {})
}

const propLabelMap: Partial<Record<keyof HoofdleidingProject, string>> = {
  aanvraagDatum: 'Aanvraagdatum',
  adres: 'Adres',
  externeReferentie: 'Externe referentie',
  gemeente: 'Gemeente',
  naam: 'Naam',
  typeWerk: 'Type werk',
  tracelengte: 'Tracelengte',
  woningen: 'Woningen',
  appartementen: 'Appartementen',
  bedrijfsunits: 'Bedrijfsunits',
  wensweek: 'Wensweek',
}

/** Ondersteunt nog niet alle (hoofdleiding) project properties. Graag toevoegen
 * wanneer nodig.
 */
export const getProjectLabel = (
  key: keyof Project | keyof HoofdleidingProject,
): string => propLabelMap[key] || key

const makeOrganisatieInGroup =
  (organisatie: string) =>
  ({ code }: GroupedFilterOption) =>
    code === organisatie

export const filterVerantwoordelijkeOptionsByOrganisatie = (
  verantwoordelijken: GroupedFilterOption[],
  organisatie?: string,
): GroupedFilterOption[] => {
  if (!organisatie) return []

  const organisatieInGroup = makeOrganisatieInGroup(organisatie)

  const userGroups = verantwoordelijken.filter(organisatieInGroup)
  const combiGroups = verantwoordelijken.filter(
    ({ group }) => group === 'Combi',
  )
  const netbeheerderGroups = verantwoordelijken.filter(
    ({ group }) => group === 'Netbeheerder',
  )

  if (combiGroups.find(organisatieInGroup))
    return [...userGroups, ...netbeheerderGroups]

  if (netbeheerderGroups.find(organisatieInGroup))
    return [...combiGroups, ...userGroups]

  return verantwoordelijken // voor Beheerders (DSP) en Aannemers
}

export const allowedDisciplinesForProject = (
  project: Project | HoofdleidingProject,
) => {
  const allowedDisciplines =
    project.type === 'Hoofdleiding' ? hlDisciplines : alDisciplines

  return project.disciplineVerzamelingen.filter((dv) =>
    allowedDisciplines.includes(dv.disciplineCode),
  )
}

export function getProjectAdres(project: Project): string {
  if (!project) return ''

  if (project.adresAttributen) {
    const { straat, huisnummer, huisnummerToevoeging, postcode, plaats } =
      project.adresAttributen

    const nummer = huisnummerToevoeging
      ? `${huisnummer} ${huisnummerToevoeging}`
      : huisnummer

    return `${straat} ${nummer}\n${postcode} ${plaats}`
  }

  if (project.adres) {
    return project.adres
  }

  return ''
}

const INFRASDO_GEBIEDSCODES = ['CG054', 'CG055', 'CG056', 'CG057']
const INFRAWERK_RIJNMOND_GEBIEDSCODES = [
  'CG068',
  'CG069',
  'CG070',
  'CG071',
  'CG072',
  'CG092',
  'CG093',
  'CG094',
]
const DUNEA_GEBIEDSCODES = ['CG054', 'CG055']
const OASEN_GEBIEDSCODES = ['CG056', 'CG057']

// Keep in sync with magSchuivenNaarAannemer in the api's util/project-util
const SCHUIVEN_NAAR_AANNEMER_GEBIEDSCODES: Record<string, string[]> = {
  Stedin: INFRASDO_GEBIEDSCODES.concat(INFRAWERK_RIJNMOND_GEBIEDSCODES),
  Dunea: DUNEA_GEBIEDSCODES,
  Oasen: OASEN_GEBIEDSCODES,
  Evides: INFRAWERK_RIJNMOND_GEBIEDSCODES,
}

export const makeIsGeschovenNaarAannemerAllowed =
  (gebiedscode?: string) =>
  ({ netbeheerderLabel }: { netbeheerderLabel: string }): boolean =>
    !!gebiedscode &&
    !!SCHUIVEN_NAAR_AANNEMER_GEBIEDSCODES[netbeheerderLabel]?.includes(
      gebiedscode,
    )

export function geschovenNaarAannemerWijzigbareDisciplines(
  {
    verantwoordelijken,
    disciplineVerzamelingen,
    gebiedscode,
  }: HoofdleidingProject,
  organisatie?: string,
) {
  const isVerantwoordelijke =
    !!organisatie && verantwoordelijken.includes(organisatie)

  return disciplineVerzamelingen
    .filter(makeIsGeschovenNaarAannemerAllowed(gebiedscode))
    .filter((dv) => isVerantwoordelijke || dv.netbeheerderCode === organisatie)
}
